"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, MessageSquare, MapPin, Sparkles, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, ArrowRight, PhoneCall } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "faq-1",
  category: "General SEO",
  question: "Why is Local SEO crucial for Stoke-on-Trent businesses specifically?",
  answer: "Stoke-on-Trent has a unique geographical setup comprising the Six Towns—Hanley, Burslem, Longton, Fenton, Tunstall, and Stoke—plus nearby Newcastle-under-Lyme and North Staffordshire areas. Local buyers search with hyper-specific geographic intent. When I optimize your web presence, I focus on local search patterns and Google Map Pack placements so high-intent local clients contact you directly instead of your regional competitors."
}, {
  id: "faq-2",
  category: "Results & Timing",
  question: "How quickly can my Stoke business expect to see local search results?",
  answer: "Most local SEO campaigns begin showing measurable movement in Google Map Pack positions and organic impressions within 3 to 6 months. Because I fix underlying technical blockers, correct local citation inconsistencies across Staffordshire directories, and optimize your Google Business Profile for real intent, early momentum often arrives faster than broad national campaigns."
}, {
  id: "faq-3",
  category: "Google Business Profile",
  question: "Do you personally manage my Google Business Profile (GBP)?",
  answer: "Yes. You work directly with me—Yassine Essaadi—not an account manager or junior assistant. I personally audit, optimize, and maintain your Google Business Profile. This includes selecting accurate primary and secondary categories, managing local geotagged photo strategy, publishing regular posts, and implementing systematic review workflows tailored to North Staffordshire customers."
}, {
  id: "faq-4",
  category: "Consultancy vs Agency",
  question: "What makes working directly with you different from hiring a local digital agency?",
  answer: "Agencies frequently sell rigid packages and delegate local SEO execution to junior team members using automated checklists. When you hire me, you get direct senior consultant access. Every strategy, audit, and local content brief is tailored specifically to your exact market positioning, high-margin services, and target competitors in Stoke-on-Trent and surrounding areas."
}, {
  id: "faq-5",
  category: "Multi-Location Targeting",
  question: "How do you target multiple towns across North Staffordshire without spammy content?",
  answer: "I build bespoke local landing page architectures designed around genuine user value rather than keyword repetition. Each page for locations like Hanley, Newcastle-under-Lyme, Crewe, or Stafford addresses authentic local customer inquiries, localized case studies, and real project showcases—protecting your domain authority while maximizing reach."
}, {
  id: "faq-6",
  category: "B2B & Industrial",
  question: "Can Local SEO help B2B manufacturers and industrial firms in Stoke-on-Trent?",
  answer: "Absolutely. Many ceramic, precision engineering, and industrial B2B companies in Staffordshire assume search engine optimization is only for local retail shops. However, procurement managers and commercial buyers actively search for terms like 'precision machining Stoke' or 'commercial logistics North Staffs'. I position your firm directly in front of these high-value decision-makers."
}, {
  id: "faq-7",
  category: "Link Building",
  question: "How do you build authentic local links and citations in Staffordshire?",
  answer: "I strictly avoid automated link networks or spammy directories that put your domain at risk. Instead, I focus on genuine local authority: securing mentions and backlinks from reputable Staffordshire business groups, regional trade associations, local news outlets, and strategic regional partners."
}, {
  id: "faq-8",
  category: "AI Search",
  question: "What is AI Search Optimization and how does it affect local search in Stoke?",
  answer: "Search engines are embedding AI Overviews and conversational search results powered by Google Gemini and ChatGPT. AI Search Optimization ensures your business entity is clearly understood by AI algorithms so your business is recommended when local searchers ask questions like 'Who is the most reliable commercial electrician in Hanley?'."
}, {
  id: "faq-9",
  category: "Reporting & ROI",
  question: "How do you track and report local search performance and lead generation?",
  answer: "I provide transparent, plain-English monthly reporting focusing on metrics that drive actual revenue: Google Map Pack rankings, organic phone calls, route direction requests, contact form submissions, and organic revenue conversion. No inflated vanity metrics or confusing technical jargon."
}, {
  id: "faq-10",
  category: "Getting Started",
  question: "How do I get started with a bespoke Stoke-on-Trent local SEO consultation?",
  answer: "Getting started is straightforward. You can book a free initial consultation directly with me. I will review your current local search footprint, inspect your primary local competitors in Staffordshire, and outline a clear, actionable roadmap to grow your local search visibility."
}];
const categories = ["All", "General SEO", "Google Business Profile", "Consultancy vs Agency", "B2B & Industrial", "Results & Timing"];
export default function Localseofaq() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const prefersReducedMotion = useReducedMotion();
  const filteredFaqs = faqList.filter(faq => {
    const matchesCategory = selectedCategory === "All" || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <section id="localseofaq" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 right-1/4 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 size-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-12 sm:mb-16">
          <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium gap-1.5">
            <MapPin className="size-3.5 text-primary" />
            <span>Stoke-on-Trent &amp; Staffordshire Market Insights</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            Local SEO Questions,{" "}
            <span className="text-primary underline decoration-accent/40 decoration-wavy underline-offset-8">
              Direct Answers
            </span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Everything you need to know about dominating Google Maps and local organic search in Stoke-on-Trent. You work directly with me—Yassine Essaadi—for honest, hands-on expert guidance.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-10 space-y-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
            <input type="text" placeholder="Search local SEO questions (e.g. Google Maps, Hanley, B2B, GBP)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-card text-card-foreground border border-border rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat, idx) => <div key={cat} data-index={idx}>
                <Button variant={selectedCategory === cat ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)} className={`rounded-full text-xs sm:text-sm transition-all ${selectedCategory === cat ? "bg-primary text-primary-foreground shadow-xs" : "border-border text-foreground hover:bg-muted"}`}>
                  {cat}
                </Button>
              </div>)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 max-w-6xl mx-auto items-start">
          <div className="lg:col-span-8">
            {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-4">
                {filteredFaqs.map((faq, i) => <motion.div key={faq.id} data-index={i} initial={prefersReducedMotion ? false : {
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: i * 0.04
            }}>
                    <AccordionItem value={faq.id} className="bg-card border border-border/80 rounded-xl px-5 py-1 shadow-2xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-xs">
                      <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-primary hover:no-underline py-4">
                        <span className="flex items-start gap-3">
                          <HelpCircle className="size-5 shrink-0 text-primary/70 mt-0.5" />
                          <span>{faq.question}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-8">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>)}
              </Accordion> : <div className="text-center py-12 px-6 bg-card border border-border rounded-xl">
                <MessageSquare className="size-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-1">No matching questions found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Try adjusting your search terms or selecting a different category filter.
                </p>
                <Button variant="outline" onClick={() => {
              setSearchQuery("");
              setSelectedCategory("All");
            }}>
                  Reset Filters
                </Button>
              </div>}
          </div>

          <div className="lg:col-span-4 sticky top-24">
            <div className="bg-card border border-border/80 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
              <div className="space-y-3">
                <Badge variant="outline" className="text-xs font-semibold text-primary border-primary/30 bg-primary/5">
                  Direct Expert Access
                </Badge>
                <h3 className="text-xl font-bold text-foreground font-default">
                  Have a specific question about your Stoke business?
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Skip the agency sales reps. Speak directly with me, Yassine Essaadi, to discuss your Google Maps visibility and local lead generation goals.
                </p>
              </div>

              <div className="space-y-3 text-xs sm:text-sm text-foreground">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>No pushy sales pitches or lock-in contracts</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Transparent audit of your North Staffs rankings</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Tailored local strategy for your high-margin services</span>
                </div>
              </div>

              <div className="pt-2 space-y-3">
                <Button asChild size="lg" className="w-full font-medium justify-between group">
                  <Link to="/ContactPage">
                    <span>Book Free Consultation</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="sm" className="w-full justify-center gap-2">
                  <a href="tel:+447490984430">
                    <PhoneCall className="size-3.5 text-primary" />
                    <span>+44 7490 984430</span>
                  </a>
                </Button>
              </div>

              <div className="pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="size-3.5 text-accent" />
                  <span>Windmill Hill Business Park, Swindon</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}